import { IQuery, useFirebaseData } from '../../@shared/hooks/useFirebaseData';
import { Collections } from '../../@core/constants/FirebasePaths';
import { Queries } from '../../@core/constants/Queries';
import { formatOrderId } from '../../@core/utils/formatOrderId';
import { OrderStatusEnum } from '../../enums/OrderStatus.enum';
import { useUserSessionStore } from '../../stores/UserSession.store';
import { IOrder } from '../../@core/interfaces/IOrder';
import { useFirebasePopulate } from '../../@shared/hooks/useFirebasePopulate';

export const useOrders = ({ loadData = false, queries = [] }) => {
  const user = useUserSessionStore((state) => state.user);
  const db = useFirebaseData<IOrder>({
    collectionPath: Collections.orders._path,
    loadData,
    queries: [...queries, ...Queries.trash],
    initialPopulate: {
      fields: ['createdBy'],
    }
  })
  const { populateItem, populateItems } = useFirebasePopulate()

  const createOrder = async (dates: number[] = [], service) => {
    const lastOrderIdPath = `${Collections.settings._path}/order`;
    const ordersPath = `${Collections.orders._path}`;
    const reservationsPath = `${Collections.reservations._path}`;

    return await db.transaction(async (transaction) => {
      const lasOrderIdSnapshot = await transaction.get(db.getReference(lastOrderIdPath));

      for (const ts of dates || []) {
        const existingSnap = await db.getDocs(
          db.getQueryRef(
            [
              {
                type: 'where',
                field: 'service',
                operator: '==',
                value: db.getReference(`${Collections.services._path}/${service?.id}`)
              },
              { type: 'where', field: 'date', operator: '==', value: ts }
            ],
            db.getCollectionRef(reservationsPath)
          )
        );
        const hasActive = existingSnap.docs
          .map((d: any) => d.data())
          .some((r: any) => r?.paymentStatus !== OrderStatusEnum.CANCELLED);
        if (hasActive) throw new Error('An order already exists for this time slot');
      }

      const lastOrderId = (lasOrderIdSnapshot.data()?.lastOrderId || 0) + 1;

      transaction.set(db.getReference(lastOrderIdPath), { lastOrderId });

      const orderId = formatOrderId({
        lastOrderId,
        lastServiceId: service?.lastServiceId || 0,
        lastLocationId: service?.details?.location?.lastLocationId || 0,
        companyId: service?.company?.companyId || 0
      })

      const generatedId = db.createId();
      const orderRefPath = `${ordersPath}/${generatedId}`;
      transaction.set(db.getReference(orderRefPath), {
        lastOrderId,
        currency: 'USD',
        total: (service?.price || 0) * (dates?.length || 1),
        orderId,
        service,
        status: OrderStatusEnum.GENERATED,
        createdAt: Date.now(),
        createdBy: db.getReference(`${Collections.users._path}/${user?.id}`),
        trash: false,
      });

      for (const ts of dates || []) {
        const d = new Date(ts);
        const day = d.toISOString().split('T')[0];
        const hour = `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
        const reservationId = db.createId();
        transaction.set(db.getReference(`${reservationsPath}/${reservationId}`), {
          order: db.getReference(orderRefPath),
          date: ts,
          day,
          hour,
          service: service,
          orderId,
          paymentStatus: OrderStatusEnum.GENERATED,
          duration: service?.details?.duration || 0,
          createdAt: Date.now(),
          createdBy: db.getReference(`${Collections.users._path}/${user?.id}`),
          trash: false,
        });
      }

      return { id: generatedId };
    });
  };

  const getOrder = async (orderId: string): Promise<IOrder> => {
    const doc = await db.docWithId(`${Collections.orders._path}/${orderId}`) as IOrder;
    return await populateItem(doc, ['createdBy']) as IOrder;
  }

  const updateOrder = async (orderId: string, data) => {
    return await db.update(orderId, data)
  }

  const getOrders = async (queries: IQuery[] = []): Promise<IOrder[]> => {
    const orders = await db.collectionWithIds([...queries, ...Queries.trash]);
    return await populateItems(orders, ['createdBy']) as IOrder[];
  }

  return {
    orders: db.data,
    createOrder,
    getOrder,
    getOrders,
    updateOrder,
    fetched: db.fetched,
    getReference: db.getReference
  }
}
